import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { getServerUrl } from "config/backend";
import { USD_DECIMALS } from "lib/numbers";
import { appendOracleNetworkParam } from "lib/oracleNetwork";
import { LEADERBOARD_PRICES_UPDATE_INTERVAL, PRICES_UPDATE_INTERVAL } from "lib/timeConstants";
import {
  getToken,
  getWrappedToken,
  NATIVE_TOKEN_ADDRESS,
  getTokenBySymbolSafe,
  getNormalizedTokenSymbol,
} from "sdk/configs/tokens";

import { TokenPricesData } from "./types";
import { useSequentialTimedSWR } from "./useSequentialTimedSWR";
import { parseContractPrice } from "./utils";
import type { TickersResponse } from "../../../lib/oracleKeeperFetcher/types";

type TokenPricesDataResult = {
  pricesData?: TokenPricesData;
  updatedAt?: number;
  error?: Error;
};

const STALE_PRICE_THRESHOLD_MS = 2 * 60 * 1000;

export function buildFallbackTokenPrices(_chainId: number): TokenPricesData {
  // Avoid synthetic default prices. Prefer explicit backend prices only.
  return {};
}

export function useTokenRecentPricesRequest(chainId: number): TokenPricesDataResult {
  const pathname = useLocation().pathname;

  // TODO temp workaround
  const refreshPricesInterval = useMemo(() => {
    return pathname.startsWith("/leaderboard") || pathname.startsWith("/competitions")
      ? LEADERBOARD_PRICES_UPDATE_INTERVAL
      : PRICES_UPDATE_INTERVAL;
  }, [pathname]);

  const { data, error } = useSequentialTimedSWR([chainId, "useTokenRecentPrices"], {
    refreshInterval: refreshPricesInterval,
    errorRetryInterval: refreshPricesInterval,
    errorRetryCount: 3,
    keepPreviousData: true,
    fetcher: async (key) => {
      const currentChainId = key[0] as number;
      const basePath = "oracle-keeper/prices/tickers";
      let path = basePath;

      const searchParams = appendOracleNetworkParam(new URLSearchParams(), currentChainId);
      if (searchParams.size > 0) {
        path = `${basePath}?${searchParams.toString()}`;
      }

      const fallbackPrices = buildFallbackTokenPrices(currentChainId);

      try {
        const response = await fetch(getServerUrl(currentChainId, path));
        if (!response.ok) {
          throw new Error(`Tickers request failed: ${response.status} ${response.statusText}`);
        }
        const priceItems = (await response.json()) as TickersResponse;
        const result: TokenPricesData = { ...fallbackPrices };

        if (!Array.isArray(priceItems)) {
          throw new Error("Invalid oracle keeper tickers response");
        }

        const nowSec = Math.floor(Date.now() / 1000);
        let oldestUpdatedAt = nowSec;
        let newestUpdatedAt = 0;

        priceItems.forEach((priceItem) => {
          let tokenConfig: any;
          try {
            tokenConfig = getToken(currentChainId, priceItem.tokenAddress);
          } catch (e) {
            const sym = priceItem.tokenSymbol ? getNormalizedTokenSymbol(priceItem.tokenSymbol) : undefined;
            if (sym) {
              tokenConfig = getTokenBySymbolSafe(currentChainId, sym);
            }
          }

          if (!tokenConfig) {
            return;
          }

          if (
            typeof priceItem.updatedAt === "number" &&
            Number.isFinite(priceItem.updatedAt) &&
            priceItem.updatedAt > 0
          ) {
            oldestUpdatedAt = Math.min(oldestUpdatedAt, priceItem.updatedAt);
            newestUpdatedAt = Math.max(newestUpdatedAt, priceItem.updatedAt);
          }

          const oracleDecimals = priceItem.oracleDecimals ?? USD_DECIMALS;
          const scaleDiff = USD_DECIMALS - oracleDecimals;
          const scaleDecimals = scaleDiff > 0 ? scaleDiff : 0;

          result[tokenConfig.address] = {
            minPrice: parseContractPrice(BigInt(priceItem.minPrice), scaleDecimals),
            maxPrice: parseContractPrice(BigInt(priceItem.maxPrice), scaleDecimals),
            updatedAt:
              typeof priceItem.updatedAt === "number" && Number.isFinite(priceItem.updatedAt) && priceItem.updatedAt > 0
                ? priceItem.updatedAt * 1000
                : undefined,
            source: priceItem.source,
          };
        });

        const wrappedToken = getWrappedToken(currentChainId);

        if (result[wrappedToken.address] && !result[NATIVE_TOKEN_ADDRESS]) {
          result[NATIVE_TOKEN_ADDRESS] = result[wrappedToken.address];
        }

        const updatedAtMs = oldestUpdatedAt * 1000;
        const ageMs = Date.now() - updatedAtMs;
        if (Number.isFinite(ageMs) && ageMs > STALE_PRICE_THRESHOLD_MS) {
          console.warn(
            "Price feed stale:",
            `oldest=${oldestUpdatedAt}`,
            `newest=${newestUpdatedAt || oldestUpdatedAt}`,
            `ageMs=${ageMs}`
          );
        }

        return {
          pricesData: result,
          updatedAt: updatedAtMs,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch token prices", currentChainId, error);
        throw error;
      }
    },
    // Avoid polling when the tab is hidden; this reduces background traffic.
    refreshWhenHidden: false,
  });

  return {
    pricesData: data?.pricesData,
    updatedAt: data?.updatedAt,
    error,
  };
}
